import React from "react";
import { http } from "../lib/api";

/** Very lightweight markdown renderer for streamed AI output.
 *  Supports: headings (## / ###), bold (**x**), bullet lists (- ), numbered lists (1.), paragraphs,
 *  and inline links `[label](url)`. When a link's URL starts with `vaku://` and an `onDeepLink`
 *  prop is provided, the link renders as a clickable pill that fires `onDeepLink(url)` instead
 *  of navigating away.
 *  No external deps – purposefully simple.
 */
export default function MarkdownLite({
  text = "",
  onDeepLink,
  requisitionMap = {},
  candidateMap = {},
  onReqUpdated,
}) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let listBuf = [];
  let listType = null;

  const inlineOpts = { onDeepLink, requisitionMap, candidateMap, onReqUpdated };

  const flushList = () => {
    if (!listBuf.length) return;
    const items = listBuf.map((l, i) => <li key={i}>{renderInline(l, inlineOpts)}</li>);
    blocks.push(
      listType === "ol" ? (
        <ol key={`l-${blocks.length}`} className="my-2 space-y-1">{items}</ol>
      ) : (
        <ul key={`l-${blocks.length}`} className="my-2 space-y-1">{items}</ul>
      )
    );
    listBuf = [];
    listType = null;
  };

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      flushList();
      return;
    }
    if (/^###\s+/.test(line)) {
      flushList();
      blocks.push(
        <h3 key={idx} className="font-display text-base font-semibold mt-3 mb-1">
          {renderInline(line.replace(/^###\s+/, ""), inlineOpts)}
        </h3>
      );
      return;
    }
    if (/^##\s+/.test(line)) {
      flushList();
      blocks.push(
        <h2 key={idx} className="font-display text-lg font-bold mt-3 mb-1">
          {renderInline(line.replace(/^##\s+/, ""), inlineOpts)}
        </h2>
      );
      return;
    }
    if (/^[-*]\s+/.test(line)) {
      if (listType !== "ul") flushList();
      listType = "ul";
      listBuf.push(line.replace(/^[-*]\s+/, ""));
      return;
    }
    if (/^\d+\.\s+/.test(line)) {
      if (listType !== "ol") flushList();
      listType = "ol";
      listBuf.push(line.replace(/^\d+\.\s+/, ""));
      return;
    }
    flushList();
    blocks.push(
      <p key={idx} className="my-1.5 leading-relaxed text-[14px]">
        {renderInline(line, inlineOpts)}
      </p>
    );
  });
  flushList();
  return <div className="stream-output">{blocks}</div>;
}

// Splits text into a mixed array of strings + [label](url) matches.
// Optionally consumes surrounding `**...**` bold markers so the LLM can emit
// `**[Name](vaku://candidate/1)**` without leaking literal asterisks around the pill.
const LINK_RE = /(\*\*)?\[([^\]]+)\]\(([^)]+)\)(\*\*)?/g;

function renderInline(text, opts) {
  const { onDeepLink, requisitionMap, candidateMap, onReqUpdated } = opts || {};
  // First split off any [label](url) links so we don't run bold-parsing across a URL.
  const tokens = [];
  let lastIdx = 0;
  let m;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > lastIdx) tokens.push({ t: "txt", v: text.slice(lastIdx, m.index) });
    const bold = !!(m[1] && m[4]); // both markers present
    tokens.push({ t: "link", label: m[2], url: m[3], bold });
    lastIdx = LINK_RE.lastIndex;
  }
  if (lastIdx < text.length) tokens.push({ t: "txt", v: text.slice(lastIdx) });

  return tokens.map((tok, i) => {
    if (tok.t === "link") {
      const isVaku = tok.url.startsWith("vaku://");
      if (isVaku && typeof onDeepLink === "function") {
        // Requisition pill? Attach hover preview if we have the record cached.
        const reqMatch = /^vaku:\/\/requisition\/(.+)$/.exec(tok.url);
        if (reqMatch && requisitionMap && requisitionMap[reqMatch[1]]) {
          return (
            <RequisitionPill
              key={i}
              url={tok.url}
              label={tok.label}
              bold={tok.bold}
              req={requisitionMap[reqMatch[1]]}
              onDeepLink={onDeepLink}
              onReqUpdated={onReqUpdated}
            />
          );
        }
        // Candidate pill? Attach hover preview if we have the record cached.
        const candMatch = /^vaku:\/\/candidate\/(.+)$/.exec(tok.url);
        if (candMatch && candidateMap && candidateMap[String(candMatch[1])]) {
          return (
            <CandidatePill
              key={i}
              url={tok.url}
              label={tok.label}
              bold={tok.bold}
              cand={candidateMap[String(candMatch[1])]}
              onDeepLink={onDeepLink}
            />
          );
        }
        return (
          <button
            key={i}
            type="button"
            data-testid={`chat-deeplink-${tok.url.replace(/[^a-z0-9]/gi, "-")}`}
            onClick={(e) => { e.preventDefault(); onDeepLink(tok.url); }}
            className={`inline-flex items-center gap-1 rounded-md bg-indigo-50 text-[#002FA7] border border-indigo-200 px-1.5 py-0.5 text-[13px] hover:bg-indigo-100 transition ${tok.bold ? "font-semibold" : "font-medium"}`}
            title={`Open ${tok.url}`}
          >
            {tok.label}
          </button>
        );
      }
      const anchor = (
        <a
          key={i}
          href={tok.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#002FA7] underline hover:opacity-80"
        >
          {tok.label}
        </a>
      );
      return tok.bold ? <strong key={i}>{anchor}</strong> : anchor;
    }
    // Plain text — run bold parsing.
    const parts = tok.v.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, j) => {
      if (/^\*\*[^*]+\*\*$/.test(p)) {
        return <strong key={`${i}-${j}`}>{p.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={`${i}-${j}`}>{p}</React.Fragment>;
    });
  });
}

// Requisition pill with hover-preview card + quick state-transition action.
function RequisitionPill({ url, label, bold, req, onDeepLink, onReqUpdated }) {
  const [open, setOpen] = React.useState(false);
  const [current, setCurrent] = React.useState(req);
  const [busy, setBusy] = React.useState(false);
  const [flash, setFlash] = React.useState(null);
  const closeTimer = React.useRef(null);

  // Keep local `current` in sync with parent-provided `req` (e.g. map reload).
  React.useEffect(() => { setCurrent(req); }, [req]);

  const stateColor = {
    Draft: "bg-slate-100 text-slate-700 border-slate-300",
    Open: "bg-emerald-50 text-emerald-800 border-emerald-300",
    Published: "bg-emerald-50 text-emerald-800 border-emerald-300",
    Closed: "bg-red-50 text-red-800 border-red-300",
  }[current.state] || "bg-slate-100 text-slate-700 border-slate-300";

  // Which state transition is offered for this pill?
  //   Draft → Open (advance)
  //   Open  → Closed (close)
  //   Published/Closed → no local action.
  let transition = null;
  if (current.state === "Draft") transition = { next: "Open", verb: "Open requisition", cta: "Open →" };
  else if (current.state === "Open") transition = { next: "Closed", verb: "Close requisition", cta: "Close" };

  const enter = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  };
  const leave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };
  React.useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  const doTransition = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!transition || busy) return;
    setBusy(true); setFlash(null);
    try {
      const r = await http.post(`/requisitions/${current.id}/state`, { state: transition.next });
      const updated = r.data || { ...current, state: transition.next };
      setCurrent(updated);
      setFlash({ kind: "ok", msg: `State is now ${updated.state}.` });
      if (typeof onReqUpdated === "function") onReqUpdated(updated);
    } catch (err) {
      setFlash({ kind: "error", msg: err.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  };

  return (
    <span
      className="relative inline-block"
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocus={enter}
      onBlur={leave}
    >
      <button
        type="button"
        data-testid={`chat-deeplink-${url.replace(/[^a-z0-9]/gi, "-")}`}
        onClick={(e) => { e.preventDefault(); onDeepLink(url); }}
        className={`inline-flex items-center gap-1 rounded-md bg-indigo-50 text-[#002FA7] border border-indigo-200 px-1.5 py-0.5 text-[13px] hover:bg-indigo-100 transition ${bold ? "font-semibold" : "font-medium"}`}
      >
        {label}
      </button>
      {open && (
        <span
          role="tooltip"
          data-testid={`chat-req-preview-${current.id}`}
          className="absolute z-50 bottom-full left-0 pb-2 w-[280px] text-[12px] text-slate-700"
        >
          <span className="block rounded-md border border-slate-200 bg-white shadow-lg p-3">
            <span className="block font-mono text-[10px] text-slate-500 mb-0.5">
              {current.requisitionNumber || current.localNumber || "(no #)"}
            </span>
            <span className="block font-display font-semibold text-slate-900 text-[13px] leading-tight mb-1.5">
              {current.title || "Untitled"}
            </span>
            <span className="flex flex-wrap gap-1.5 mb-2">
              <span
                data-testid={`chat-req-preview-state-${current.id}`}
                className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold ${stateColor}`}
              >
                {current.state || "—"}
              </span>
              {current.department && (
                <span className="inline-block rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-[10px] text-slate-700">
                  {current.department}
                </span>
              )}
              {current.location && (
                <span className="inline-block rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-[10px] text-slate-700">
                  {current.location}
                </span>
              )}
            </span>
            <span className="block text-[11px] text-slate-600 mb-2">
              <span className="font-semibold text-slate-800">{current.openings ?? 1}</span> opening{(current.openings ?? 1) === 1 ? "" : "s"}
              {current.hiringManager ? <> · HM: <span className="text-slate-800">{current.hiringManager}</span></> : null}
            </span>

            {/* Quick state transition */}
            {transition ? (
              <button
                type="button"
                data-testid={`chat-req-state-toggle-${current.id}`}
                onClick={doTransition}
                disabled={busy}
                title={transition.verb}
                className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold border transition ${
                  transition.next === "Open"
                    ? "bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700"
                    : "bg-red-600 text-white border-red-700 hover:bg-red-700"
                } disabled:opacity-60 disabled:cursor-wait`}
              >
                {busy ? "Saving…" : transition.cta}
              </button>
            ) : (
              <span className="block text-[10px] italic text-slate-400">
                {current.state === "Closed" ? "Closed — no further transitions." : "Published — manage in Requisitions."}
              </span>
            )}

            {flash && (
              <span
                data-testid={`chat-req-state-flash-${current.id}`}
                className={`block mt-2 text-[10px] rounded px-1.5 py-1 border ${
                  flash.kind === "error"
                    ? "bg-red-50 text-red-800 border-red-300"
                    : "bg-emerald-50 text-emerald-800 border-emerald-300"
                }`}
              >
                {flash.msg}
              </span>
            )}
          </span>
        </span>
      )}
    </span>
  );
}


// Candidate pill with hover-preview card.
function CandidatePill({ url, label, bold, cand, onDeepLink }) {
  const [open, setOpen] = React.useState(false);
  const statusColor = {
    New: "bg-blue-50 text-blue-800 border-blue-300",
    Screening: "bg-amber-50 text-amber-800 border-amber-300",
    Interview: "bg-indigo-50 text-indigo-800 border-indigo-300",
    Offer: "bg-purple-50 text-purple-800 border-purple-300",
    Hired: "bg-emerald-50 text-emerald-800 border-emerald-300",
    Rejected: "bg-red-50 text-red-800 border-red-300",
  }[cand.status] || "bg-slate-100 text-slate-700 border-slate-300";

  const score = Number.isFinite(cand.matchScore) ? cand.matchScore : 0;
  const scoreColor = score >= 85 ? "bg-emerald-500" : score >= 70 ? "bg-indigo-500" : score >= 50 ? "bg-amber-500" : "bg-red-500";

  const snippetSource = cand.profileSummary || cand.resumeText || cand.experience || "";
  const snippet = snippetSource.trim().replace(/\s+/g, " ").slice(0, 160);

  // Pick the freshest activity timestamp we have.
  const activityCandidates = [
    ["updated", cand.resumeUpdatedAt],
    ["LinkedIn", cand.linkedinRefreshedAt],
    ["applied", cand.applicationDate],
  ].filter(([, v]) => !!v);
  let activity = null;
  if (activityCandidates.length) {
    // Sort desc by parsed time.
    activityCandidates.sort((a, b) => new Date(b[1]) - new Date(a[1]));
    const [kind, iso] = activityCandidates[0];
    const dt = new Date(iso);
    activity = { kind, label: isNaN(dt.getTime()) ? iso : dt.toLocaleDateString() };
  }

  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid={`chat-deeplink-${url.replace(/[^a-z0-9]/gi, "-")}`}
        onClick={(e) => { e.preventDefault(); onDeepLink(url); }}
        className={`inline-flex items-center gap-1 rounded-md bg-indigo-50 text-[#002FA7] border border-indigo-200 px-1.5 py-0.5 text-[13px] hover:bg-indigo-100 transition ${bold ? "font-semibold" : "font-medium"}`}
      >
        {label}
      </button>
      {open && (
        <span
          role="tooltip"
          data-testid={`chat-cand-preview-${cand.id}`}
          className="absolute z-50 bottom-full left-0 mb-2 w-[300px] rounded-md border border-slate-200 bg-white shadow-lg p-3 text-[12px] text-slate-700"
          style={{ pointerEvents: "none" }}
        >
          <span className="block font-display font-semibold text-slate-900 text-[13px] leading-tight">
            {cand.name || "(no name)"}
          </span>
          {cand.position && (
            <span className="block text-[11px] text-slate-500 mb-1.5">{cand.position}{cand.location ? ` · ${cand.location}` : ""}</span>
          )}
          <span className="flex items-center gap-1.5 mb-2">
            <span className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold ${statusColor}`}>
              {cand.status || "—"}
            </span>
            <span className="flex-1 flex items-center gap-1.5">
              <span className="flex-1 h-1.5 bg-slate-100 rounded overflow-hidden">
                <span className={`block h-full ${scoreColor}`} style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
              </span>
              <span className="text-[10px] font-mono text-slate-600">{score}/100</span>
            </span>
          </span>
          {snippet ? (
            <span className="block text-[11px] leading-snug text-slate-600 mb-2 line-clamp-3">
              {snippet}{snippetSource.length > 160 ? "…" : ""}
            </span>
          ) : (
            <span className="block text-[11px] italic text-slate-400 mb-2">No resume snippet on file.</span>
          )}
          {activity && (
            <span className="block text-[10px] font-mono text-slate-500">
              Last {activity.kind}: {activity.label}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
